import React from "react";
import {useSelector} from "react-redux";
import {IStore} from "@/redux/defaultStore";
import {OWGame} from "@devour/client";
import useGetOverwolfGameInfo from "@/hooks/useGetOverwolfGameInfo";
import useGetOverwolfGameEventsDisabled from "@/hooks/useGetOverwolfGameEventsDisabled";
import classNames from "classnames";
import {capitalizeFirstLetter} from "@/utils/formatString/capitalizeFirstLetter";
import {
    overwolfGameInfo,
    calculateGameStatus,
    getAbbreviatedStatusText,
    GameStatusType,
} from "@/hooks/useOverwolfInterop";

const getWarningMessage = (gameInfo: overwolfGameInfo, devourGameInfo: OWGame, gameEventsDisabled: boolean, gameEventState: number): string | undefined => {
    // game is running but gep registration failed
    if (gameInfo?.gepStarted === false && gameInfo?.isGameRunning) {
        return " (Issue detecting game events)";
    }
    if (devourGameInfo?.isEventSupported && gameEventsDisabled) {
        return ` (quests${gameEventState === 2 ? "may be " : " "}off)`;
    }
    return "";
};

interface OverwolfGameStatusChipProps {
    onClick?: () => void;
    compact?: boolean; // Use abbreviated text for mobile/compact views
}

const OverwolfGameStatusChip = React.memo<OverwolfGameStatusChipProps>(({ onClick, compact = false }) => {
    const gameInfo = useSelector((store: IStore) => store.metaStore.overwolfGameInfo);
    const { data: devourGameInfo } = useGetOverwolfGameInfo(gameInfo?.gameId);
    const { gameEventsDisabled, state} = useGetOverwolfGameEventsDisabled(gameInfo?.gameId);

    // Enhance gameInfo with GEP status for our calculation
    const enhancedGameInfo = gameInfo ? {
        ...gameInfo,
        gameEventsDisabled,
        overwolfGameState: state,
        gepStatus: gameInfo.gepStarted && !gameEventsDisabled && state === 1 ? "healthy" as const
            : gameEventsDisabled || state === 0 || state === 3 ? "offline" as const
                : "degraded" as const,
    } : null;


    const gameStatus = calculateGameStatus(enhancedGameInfo);
    const warning = getWarningMessage(gameInfo, devourGameInfo, gameEventsDisabled, state);

    // Determine display text
    const getDisplayText = (): string => {
        if (compact) {
            return getAbbreviatedStatusText(gameStatus);
        }

        if (!gameInfo) {
            return "Game not detected";
        }

        return `In ${capitalizeFirstLetter(gameInfo.gameTitle)} ${warning}`;
    };

    return (
        <div
            className={classNames("overwolf-govip_rewards_tag", {
                "overwolf-govip_rewards_tag_active": gameStatus === "green",
                "overwolf-govip_rewards_tag_warning": gameStatus === "yellow" || gameStatus === "red",
                "overwolf-govip_rewards_tag_gray": gameStatus === "gray",
            })}
            onClick={onClick}
            style={onClick ? { cursor: "pointer" } : undefined}
        >
            <div
                className={classNames("circle", {
                    circle_active: gameStatus === "green",
                    circle_warning: gameStatus === "yellow" || gameStatus === "red",
                    circle_gray: gameStatus === "gray",
                })}
            />
            {getDisplayText()}
        </div>
    );
});

OverwolfGameStatusChip.displayName = "OverwolfGameStatusChip";

export default OverwolfGameStatusChip;